import {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useRef,
  type FC,
  type ReactNode,
} from "react";
import { fromEvent, throttleTime, map, filter, asyncScheduler } from "rxjs";

import { useActiveOrganization } from "common/account/active_organization";
import { segmentTrack } from "util/segment";

export enum IdentityTabV2AnalyticsEvent {
  HIGH_RISK_SIGNER_VIEWED = "High risk signer viewed",
  VIEW_PROFILE_CLICKED = "View profile clicked",
  SCROLL_BEHAVIOR = "Scroll behavior",
  DOWNLOAD_REPORT = "Report downloaded",
}

type TrackFunction =
  | ((event: IdentityTabV2AnalyticsEvent, data?: Record<string, unknown>) => void)
  | null;

const IdentityTabV2AnalyticsContext = createContext<TrackFunction | null>(null);

export const IdentityTabV2AnalyticsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [activeOrganizationId] = useActiveOrganization();
  const scrollStateRef = useRef({
    lastPosition: 0,
    totalScrolled: 0,
    hasScrolled: false,
  });
  const appElementRef = useRef<HTMLElement | null>(null);

  const track = useCallback(
    (event: IdentityTabV2AnalyticsEvent, data?: Record<string, unknown>) => {
      segmentTrack(`Identity Tab V2 - ${event}`, {
        ...data,
        orgId: activeOrganizationId,
      });
    },
    [],
  );

  const generateScrollData = useCallback(() => {
    const appElement = appElementRef.current;
    if (!appElement) {
      return null;
    }

    const currentPosition = appElement.scrollTop;
    const previousPosition = scrollStateRef.current.lastPosition;

    return {
      position: currentPosition,
      direction: currentPosition > previousPosition ? "down" : "up",
      totalScrolled: scrollStateRef.current.totalScrolled,
      percentage: Math.round(
        (currentPosition / (appElement.scrollHeight - appElement.clientHeight || 1)) * 100,
      ),
    };
  }, []);

  useEffect(() => {
    // Get the app element
    const appElement = document.getElementById("app");
    if (!appElement) {
      return;
    }

    appElementRef.current = appElement;
    scrollStateRef.current.lastPosition = appElement.scrollTop;

    const subscription = fromEvent(appElement, "scroll", { capture: true })
      .pipe(
        map(() => {
          const currentPosition = appElement.scrollTop;
          const previousPosition = scrollStateRef.current.lastPosition;
          const scrollDelta = Math.abs(currentPosition - previousPosition);

          scrollStateRef.current = {
            lastPosition: currentPosition,
            totalScrolled: scrollStateRef.current.totalScrolled + scrollDelta,
            hasScrolled: true,
          };

          return {
            position: currentPosition,
            direction: currentPosition > previousPosition ? "down" : "up",
            scrollDelta,
            percentage: Math.round(
              (currentPosition / (appElement.scrollHeight - appElement.clientHeight || 1)) * 100,
            ),
          };
        }),
        throttleTime(10000, asyncScheduler, { leading: false, trailing: true }),
        filter(() => {
          return scrollStateRef.current.hasScrolled && scrollStateRef.current.totalScrolled > 50;
        }),
        map((data) => {
          const totalScrolled = scrollStateRef.current.totalScrolled;

          scrollStateRef.current.totalScrolled = 0;
          scrollStateRef.current.hasScrolled = false;

          return {
            ...data,
            totalScrolled,
          };
        }),
      )
      .subscribe((scrollData) => {
        track(IdentityTabV2AnalyticsEvent.SCROLL_BEHAVIOR, scrollData);
      });

    return () => {
      if (scrollStateRef.current.hasScrolled && scrollStateRef.current.totalScrolled > 50) {
        const finalScrollData = generateScrollData();
        if (finalScrollData) {
          track(IdentityTabV2AnalyticsEvent.SCROLL_BEHAVIOR, finalScrollData);
        }
      }

      subscription.unsubscribe();
    };
  }, [track]);

  return (
    <IdentityTabV2AnalyticsContext.Provider value={track}>
      {children}
    </IdentityTabV2AnalyticsContext.Provider>
  );
};

export function useIdentityTabV2Analytics(): TrackFunction {
  return useContext(IdentityTabV2AnalyticsContext);
}
